import { Fragment, useContext, useEffect, useRef, useState } from "react";
import { LanguageContext } from "../../../../context/LanguageContext";
import moment from "moment";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import BusinessLoader from "../../../common/BusinessLoader";
import { useSnackbar } from "../../../../context/SnackbarContext";
import { useAuthContext } from "../../../../context/AuthContext";
import { GETWALLETSENTMONEY } from "../../../../apis/configuration";
import { handleApi } from "../../../../apis";
import { downloadReceiptImage, printWalletReceiptPreview, copyReceiptImage } from "../../../../utils";
import UCashTransactionSuccess from "../../../common/UCashTransactionSuccess";
import UCashTransactionReceipt from "../../../common/UCashTransactionReceipt";
import DownloadReceipt from "../../../common/DownloadReceipt";
import { formatNum } from "../../../../oscar-pos-core/constants";

const SendMoneyTransactionSuccess = () => {
  const [I18n] = useContext(LanguageContext);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);
  const [isPrintLoading, setIsPrintLoading] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [transactionDetail, setTransactionDetail] = useState({});
  const [state, setState] = useState({
    from: "",
    to: "",
    type: "",
    amount: 0,
    date: moment(),
    bankLogo: "",
    commission: 0,
  });

  const receiptRef = useRef();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const { business } = useAuthContext();

  const timeoutId = useRef(null);
  const isMounted = useRef(true);

  useEffect(() => {
    fetchWalletSendmoneyTransaction();
    return () => {
      isMounted.current = false;
      if (timeoutId.current) clearTimeout(timeoutId.current);
    };
  }, []);

  const fetchWalletSendmoneyTransaction = async () => {
    if (location?.state?.data && location?.state?.transaction) {
      setState({ ...location.state.data });
      setTransactionDetail({ ...location.state.transaction });
      return;
    }
    setIsLoading(true);
    try {
      const response = await handleApi(
        GETWALLETSENTMONEY,
        { job_id: params?.id },
        undefined,
        navigate,
      );
      if (!isMounted.current) return;
      if (response?.status === "COMPLETED") {
        if (timeoutId.current) clearTimeout(timeoutId.current);
        const transaction = response?.transaction_details || {};
        setTransactionDetail(transaction);
        setState({
          from: `${transaction?.sender_name}(****${transaction?.sender_account?.slice(-4)})`,
          amount: Math.abs(transaction?.amount),
          to: `${transaction?.account_title}(****${transaction?.account_number})`,
          bankLogo: transaction?.logo_link,
          type: transaction?.type,
          date: transaction?.created_at,
          commission: transaction?.commission,
        });
        setIsLoading(false);
      } else if (response?.status === "FAILED") {
        if (timeoutId.current) clearTimeout(timeoutId.current);
        setIsLoading(false);
        showSnackbar({
          message: response?.message || I18n.error_in_send_money,
          severity: "error",
          icon: "",
        });
        navigate("/UCash");
      } else {
        timeoutId.current = setTimeout(fetchWalletSendmoneyTransaction, 3000);
      }
    } catch (error) {
      if (!isMounted.current) return;
      if (timeoutId.current) clearTimeout(timeoutId.current);
      showSnackbar({
        message: error?.response?.data?.message || I18n.error_in_send_money,
        severity: "error",
        icon: "",
      });
      setIsLoading(false);
      navigate("/UCash");
    }
  };

  const handleDownload = () =>
    downloadReceiptImage(receiptRef, {
      setIsDownloadLoading,
      analyticsEvent: "w_send_download",
      fileName: "Rupin Transaction Receipt",
    });

  const handleCopy = () =>
    copyReceiptImage(receiptRef, { showSnackbar, I18n, analyticsEvent: "w_send_copy_receipt" });

  const tid = transactionDetail?.id || "";
  const recipientName = transactionDetail?.account_title || "";
  const bankLast4 = (transactionDetail?.account_number || "").slice(-4);
  const bankDisplayName =
    transactionDetail?.account_bank || "";
  const bankInfo = bankLast4
    ? `${bankDisplayName ? `${bankDisplayName} – ` : ""}${bankLast4}`
    : "";
  const senderInitial = (transactionDetail?.sender_name || "")
    .charAt(0)
    .toUpperCase();
  const formattedDate = state?.date
    ? moment(state.date).format("D MMMM YYYY, hh:mm A")
    : "";

  const receiptRows = [
    { label: I18n.by, value: business?.name || "-" },
    { label: I18n.tid, value: tid },
    { label: I18n.date, value: formattedDate },
    { label: I18n.fee, value: `Rs. ${state.commission || 0} ${I18n.fee_free}` },
    {
      label: I18n.total_amount,
      value: `Rs. ${formatNum(state.amount) || 0}`,
    },
  ];

  const receiptHeaderText = (
    <>
      {I18n.money_sent_with} <strong>{I18n.rupin}</strong>
    </>
  );

  const successSubtitle = recipientName
    ? `${I18n.money_sent_to} ${recipientName}${bankLast4 ? ` - ${bankLast4}` : ""}`
    : "";

  const handlePrint = () =>
    printWalletReceiptPreview(
      business,
      {
        headerText: `${I18n.money_sent_with} <strong>${I18n.rupin}</strong>`,
        amount: state.amount,
        recipientName,
        bankInfo,
        rows: receiptRows,
      },
      { showSnackbar, setIsPrintLoading, analyticsEvent: "w_send_print" },
    );

  if (isLoading) return <BusinessLoader loadBusiness />;

  return (
    <Fragment>
      {showReceipt ? (
        <UCashTransactionReceipt
          amount={state.amount}
          headerText={receiptHeaderText}
          recipientName={recipientName}
          bankInfo={bankInfo}
          rows={receiptRows}
          senderInitial={senderInitial}
          onBack={() => setShowReceipt(false)}
          onPrint={handlePrint}
          isPrintLoading={isPrintLoading}
          onCopy={handleCopy}
        />
      ) : (
        <UCashTransactionSuccess
          recipientName={recipientName}
          recipientSuffix={bankLast4}
          tid={tid}
          amount={state.amount}
          onViewReceipt={() => {
            triggerLogEvent(analytics, "w_send_view_receipt");
            setShowReceipt(true);
          }}
          onClose={() => {
            triggerLogEvent(analytics, "w_send_back_to_wallet");
            navigate(location.state?.backPath || "/UCash");
          }}
          onDownload={handleDownload}
          isDownloadLoading={isDownloadLoading}
          onBack={() => navigate(location.state?.backPath || "/UCash")}
          processingTime="2.6 sec"
        />
      )}

      <DownloadReceipt
        ref={receiptRef}
        amount={state.amount}
        headerText={receiptHeaderText}
        recipientName={recipientName}
        bankInfo={bankInfo}
        rows={receiptRows}
      />
    </Fragment>
  );
};

export default SendMoneyTransactionSuccess;
